import type { Ref } from 'vue'

import type {
  ApiRequestOptions,
  ClientDeleteRequestOptions,
  ClientGetRequestOptions,
  ClientPatchRequestOptions,
  ClientPostRequestOptions,
  ClientPutRequestOptions
} from '@/services/api/types.ts'

import { isServerError, reportServerError } from '@/domains/notifications/server-error/reportServerError'
import { fetchClient } from '@/services/api/fetchClient.ts'
import { buildUrl } from '@/utils/url-util/urlUtil.ts'

/**
 * localStorage key holding the SPA bearer token. Exported because anything that
 * talks to the API outside this client — the RBAC abilities transport, which runs
 * before the app mounts — needs the same token, and a second literal is a second
 * thing to keep in step.
 */
export const AUTH_TOKEN_STORAGE_KEY = 'auth_token'

export class ApiClient {
  private baseURL: string
  private pendingRequests = new Map<string, AbortController>()
  private static responseObservers = new Set<(response: Response) => void>()

  constructor(baseURL: string) {
    this.baseURL = baseURL
  }

  /**
   * Register a callback invoked with every resolved response (success or
   * error). Used for cross-cutting concerns like RBAC version-drift detection.
   * Returns an unsubscribe function.
   */
  static onResponse(observer: (response: Response) => void): () => void {
    ApiClient.responseObservers.add(observer)
    return () => {
      ApiClient.responseObservers.delete(observer)
    }
  }

  // TResponse/TQuery must default to `any`, not `unknown`/`Record<string, unknown>`: this
  // client is called from ~70 domain services, most of which don't pass explicit generics
  // and rely on `any`'s bidirectional assignability (e.g. QueryParams<TQuery> collapses to
  // `{ [x: string]: never }` under a `Record<string, unknown>` default, rejecting every real
  // caller's queryParams object; response `data` similarly needs to unify with whatever
  // concrete DTO type the caller's `.data.value` is used as). Verified via a full vue-tsc
  // pass that switching these to unknown/Record broke 70+ files across the app.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see comment above
  async request<TResponse = any, TBody = unknown, TQuery = any>(
    endpoint: string,
    {
      method = 'GET',
      headers = {},
      body,
      queryParams,
      auth,
      baseURL,
      preventConcurrent = true,
      token,
      responseType = 'json'
    }: ApiRequestOptions<TBody, TQuery> = {}
  ): Promise<{
    data: Ref<TResponse | null>
    response: Ref<Response | null>
    execute: () => Promise<unknown>
    abort: () => void
  }> {
    // Generate a token if not provided and preventConcurrent is enabled
    if (preventConcurrent && !token) {
      token = `${endpoint}-${method}-${JSON.stringify(queryParams || {})}`
    }

    // Create a new AbortController for this request
    const controller = new AbortController()

    // If we're preventing concurrent requests with the same token
    if (preventConcurrent && token) {
      // Abort any existing request with the same token
      if (this.pendingRequests.has(token)) {
        this.pendingRequests.get(token)?.abort()
        this.pendingRequests.delete(token)
      }

      // Store the new controller
      this.pendingRequests.set(token, controller)
    }

    // FormData (multipart uploads) must not be JSON-encoded, and the browser
    // has to set the `Content-Type` itself so the multipart boundary is added.
    const isFormData = body instanceof FormData
    const hasBody = body !== undefined && body !== null

    const finalHeaders: Record<string, string> = {
      // `Content-Type` describes a request body, so a GET, or a DELETE without one, has none to describe.
      // Sending it anyway also makes a cross-origin request non-simple, which forces a CORS
      // preflight that a signed storage URL does not answer.
      ...(hasBody && !isFormData ? { 'Content-Type': 'application/json' } : {}),
      Accept: responseType === 'blob' ? 'application/octet-stream' : 'application/json',
      ...headers
    }

    if (auth) {
      const authToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
      if (authToken) {
        finalHeaders['Authorization'] = `Bearer ${authToken}`
      }
    }

    // Use the instance's baseURL if not overridden
    const finalBaseURL = baseURL || this.baseURL
    const url = buildUrl(finalBaseURL, endpoint, queryParams)

    let requestBody: BodyInit | undefined
    if (body instanceof FormData) {
      requestBody = body
    } else if (body !== undefined && body !== null) {
      requestBody = JSON.stringify(body)
    }

    // Use the customFetch instance to make the request
    const fetchResult = fetchClient<TResponse>(url, {
      method,
      headers: finalHeaders,
      body: requestBody,
      signal: controller.signal,
      preventConcurrent,
      token,
      responseType
    })

    // Wait for the fetch to complete before checking the result
    await fetchResult

    const { data, error, execute, abort, response } = fetchResult

    // Check if the request was aborted (e.g., navigation away, concurrent request deduplication)
    if (controller.signal.aborted) {
      if (preventConcurrent && token) {
        this.pendingRequests.delete(token)
      }
      throw new DOMException('Request aborted', 'AbortError')
    }

    // Notify response observers (e.g. RBAC drift detection) for any resolved
    // response, success or error.
    if (response.value) {
      ApiClient.responseObservers.forEach(observer => observer(response.value as Response))
    }

    // Check for errors
    if (!response.value?.ok) {
      const status = response.value?.status

      // Clean up on error if preventConcurrent is enabled
      if (preventConcurrent && token) {
        this.pendingRequests.delete(token)
      }

      // If a request with auth receives a 401 Unauthorized, clear stale auth token
      if (status === 401 && auth) {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('auth_token_expires_at')
      }

      // Every service reaches the server through this method, so a server error is reported
      // once here rather than in each of the ~70 callers. Callers still get the throw below
      // and stay free to handle the failure their own way.
      if (isServerError(status)) {
        reportServerError({ status, url, body: fetchResult.parsedErrorData })
      }

      // Preserve Laravel's structured error response
      // Get the parsed error data that was stored in fetchClient
      const errorData = fetchResult.parsedErrorData || data.value || error.value

      const errorMessage = errorData?.message || 'An error occurred'

      const structuredError = new Error(errorMessage)
      // Attach the full Laravel error structure and HTTP status to the error object
      Object.assign(structuredError, { data: errorData, status })

      throw structuredError
    }

    // Create an enhanced abort function that also cleans up the pendingRequests map
    const enhancedAbort = () => {
      if (preventConcurrent && token) {
        this.pendingRequests.delete(token!)
      }
      controller.abort()
      abort()
    }

    // Return with the enhanced abort function
    return {
      data,
      response,
      execute,
      abort: enhancedAbort
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see rationale on request() above
  get<TResponse = any, TQuery = any>({
    endpoint,
    queryParams,
    auth = false,
    baseURL,
    preventConcurrent,
    token,
    responseType
  }: ClientGetRequestOptions<TQuery>) {
    return this.request<TResponse>(endpoint, {
      method: 'GET',
      queryParams,
      auth,
      baseURL,
      preventConcurrent,
      token,
      responseType
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see rationale on request() above
  post<TResponse = any, TBody = unknown>({
    endpoint,
    body,
    auth = false,
    baseURL,
    preventConcurrent,
    token
  }: ClientPostRequestOptions<TBody>) {
    return this.request<TResponse, TBody>(endpoint, {
      method: 'POST',
      body,
      auth,
      baseURL,
      preventConcurrent,
      token
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see rationale on request() above
  put<TResponse = any, TBody = unknown>({
    endpoint,
    body,
    auth = false,
    baseURL,
    preventConcurrent,
    token
  }: ClientPutRequestOptions<TBody>) {
    return this.request<TResponse, TBody>(endpoint, {
      method: 'PUT',
      body,
      auth,
      baseURL,
      preventConcurrent,
      token
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see rationale on request() above
  patch<TResponse = any, TBody = unknown>({
    endpoint,
    body,
    auth = false,
    baseURL,
    preventConcurrent,
    token
  }: ClientPatchRequestOptions<TBody>) {
    return this.request<TResponse, TBody>(endpoint, {
      method: 'PATCH',
      body,
      auth,
      baseURL,
      preventConcurrent,
      token
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see rationale on request() above
  delete<TResponse = any, TQuery = any, TBody = undefined>({
    endpoint,
    queryParams,
    body,
    auth = false,
    baseURL,
    preventConcurrent,
    token
  }: ClientDeleteRequestOptions<TQuery, TBody>) {
    return this.request<TResponse, TBody, TQuery>(endpoint, {
      method: 'DELETE',
      queryParams,
      body,
      auth,
      baseURL,
      preventConcurrent,
      token
    })
  }
}
